import {
  BarChart, Box, BreadcrumbGroup, Button, ColumnLayout, Header, KeyValuePairs,
  SpaceBetween, Spinner, StatusIndicator, Table, Tabs,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAuthToken } from './auth';
import { useDocumentTitle } from './useDocumentTitle';
import { getAwsCreds } from './awsCredentials';
import { apiFetch } from './apiFetch';

function statusType(s: string): 'success' | 'error' | 'in-progress' | 'warning' | 'info' {
  if (/COMPLETE$/.test(s) && !/ROLLBACK|DELETE/.test(s)) return 'success';
  if (/_FAILED$/.test(s)) return 'error';
  if (/_IN_PROGRESS$/.test(s)) return 'in-progress';
  if (/ROLLBACK|DELETE/.test(s)) return 'warning';
  return 'info';
}

interface Resource {
  LogicalResourceId: string;
  PhysicalResourceId: string;
  ResourceType: string;
  ResourceStatus: string;
  LastUpdatedTimestamp: string;
}

interface CostRow {
  service: string;
  cost: string;
  period: string;
}

export default function StackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiKey = getAuthToken();

  const [stack, setStack] = useState<any>(null);
  useDocumentTitle(stack?.name);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [resources, setResources] = useState<Resource[]>([]);
  const [resLoading, setResLoading] = useState(false);
  const [resError, setResError] = useState<string | null>(null);

  const [costRows, setCostRows] = useState<CostRow[]>([]);
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);
  const [costFetched, setCostFetched] = useState(false);

  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invFetched, setInvFetched] = useState(false);

  useEffect(() => {
    if (!apiKey || !id) { setLoading(false); return; }
    apiFetch(`/stacks/${id}?populate[account][fields][0]=Alias&populate[account][fields][1]=accountid&populate[project][fields][0]=name&populate[project][fields][1]=documentId&populate[tags][fields][0]=key&populate[tags][fields][1]=value`)
      .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); })
      .then(json => setStack(json.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [apiKey, id]);

  const fetchResources = () => {
    if (!apiKey || !id) return;
    setResLoading(true);
    setResError(null);
    const headers: Record<string, string> = {};
    if (stack?.account?.Alias) {
      const creds = getAwsCreds(stack.account.Alias);
      if (creds) {
        headers['x-aws-access-key-id'] = creds.accessKeyId;
        headers['x-aws-secret-access-key'] = creds.secretAccessKey;
        headers['x-aws-session-token'] = creds.sessionToken;
      }
    }
    apiFetch(`/stacks/${id}/resources`, { headers })
      .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); })
      .then(json => {
        const data = Array.isArray(json) ? json : (json.data ?? []);
        setResources(data);
      })
      .catch(e => setResError(e.message))
      .finally(() => setResLoading(false));
  };

  const fetchCosts = () => {
    if (!apiKey || !id) return;
    if (!stack?.account?.Alias) {
      setCostError('AWS credentials required for cost data');
      return;
    }
    const creds = getAwsCreds(stack.account.Alias);
    if (!creds) {
      setCostError('AWS credentials required for cost data');
      return;
    }
    setCostLoading(true);
    setCostError(null);
    const headers: Record<string, string> = {
      'x-aws-access-key-id': creds.accessKeyId,
      'x-aws-secret-access-key': creds.secretAccessKey,
      'x-aws-session-token': creds.sessionToken,
    };
    apiFetch(`/cost/stack/${id}`, { headers })
      .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); })
      .then(json => {
        const rows: CostRow[] = [];
        for (const result of json.results ?? []) {
          const period = `${result.TimePeriod.Start} – ${result.TimePeriod.End}`;
          for (const group of result.Groups ?? []) {
            rows.push({
              service: group.Keys[0],
              cost: parseFloat(group.Metrics.UnblendedCost.Amount).toFixed(2),
              period,
            });
          }
        }
        setCostRows(rows);
      })
      .catch(e => setCostError(e.message))
      .finally(() => setCostLoading(false));
    setCostFetched(true);
  };

  const fetchInventory = async () => {
    if (!apiKey || !id) return;
    setInvLoading(true);
    const types = [
      { endpoint: 'ec2-instances', assetType: 'ec2-instance', nameField: 'resourceId' },
      { endpoint: 'ecs-services', assetType: 'ecs-service', nameField: 'serviceName' },
      { endpoint: 'lambda-functions', assetType: 'lambda-function', nameField: 'functionName' },
      { endpoint: 'rds-instances', assetType: 'rds-instance', nameField: 'dbInstanceIdentifier' },
      { endpoint: 'dynamodb-tables', assetType: 'dynamodb-table', nameField: 'tableName' },
      { endpoint: 'api-gateways', assetType: 'api-gateway', nameField: 'apiName' },
    ];
    const results = await Promise.all(types.map(async ({ endpoint, assetType, nameField }) => {
      try {
        const res = await apiFetch(
          `/${endpoint}?filters[stack][documentId]=${id}&fields[0]=${nameField}&fields[1]=status&pagination[pageSize]=100`
        );
        if (!res.ok) return [];
        const json = await res.json();
        return (json.data ?? []).map((item: any) => ({
          documentId: item.documentId,
          name: item[nameField] || item.name || '—',
          type: assetType,
          status: item.status || '—',
        }));
      } catch { return []; }
    }));
    setInventoryItems(results.flat());
    setInvLoading(false);
    setInvFetched(true);
  };

  useEffect(() => {
    if (stack) fetchResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stack]);

  const handleTabChange = (tabId: string) => {
    if (tabId === 'costs' && !costFetched) fetchCosts();
    if (tabId === 'inventory' && !invFetched) fetchInventory();
  };

  const totalCost = costRows.reduce((sum, r) => sum + parseFloat(r.cost), 0).toFixed(2);

  if (loading) return <Spinner size="large" />;
  if (error) return <Box color="text-status-error" fontSize="heading-m">Error: {error}</Box>;
  if (!stack) return <Box color="text-body-secondary">Stack not found</Box>;

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: 'Home', href: '/' },
          { text: 'Stacks', href: '/stacks' },
          { text: stack.name, href: '#' },
        ]}
        onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
      />
      <Header
        variant="h1"
        description={<StatusIndicator type={statusType(stack.stack_status)}>{stack.stack_status}</StatusIndicator>}
      >
        {stack.name}
      </Header>
      <ColumnLayout columns={3}>
        <KeyValuePairs items={[
          { label: 'Region', value: stack.Region || '—' },
          { label: 'ARN', value: stack.arn || '—' },
          { label: 'Account', value: stack.account ? `${stack.account.Alias} (${stack.account.accountid})` : '—' },
          { label: 'Project', value: stack.project ? <Link to={`/projects/${stack.project.documentId}`}>{stack.project.name}</Link> : '—' },
          { label: 'Created', value: stack.CreationTime ? new Date(stack.CreationTime).toLocaleString() : '—' },
          { label: 'Last Updated', value: stack.LastUpdatedTime ? new Date(stack.LastUpdatedTime).toLocaleString() : '—' },
          { label: 'Description', value: stack.Description || '—' },
        ]} />
      </ColumnLayout>
      <Tabs onChange={({ detail }) => handleTabChange(detail.activeTabId)} tabs={[
        {
          id: 'tags', label: 'Tags',
          content: (
            <Table variant="embedded" items={stack.tags ?? []}
              columnDefinitions={[
                { id: 'key', header: 'Key', cell: (i: any) => i.key },
                { id: 'value', header: 'Value', cell: (i: any) => i.value },
              ]}
              empty={<Box textAlign="center" color="text-body-secondary">No tags</Box>}
            />
          ),
        },
        {
          id: 'outputs', label: 'Outputs',
          content: (
            <Table variant="embedded" items={stack.Outputs ?? []}
              columnDefinitions={[
                { id: 'key', header: 'Key', cell: (i: any) => i.OutputKey },
                { id: 'value', header: 'Value', cell: (i: any) => i.OutputValue },
              ]}
              empty={<Box textAlign="center" color="text-body-secondary">No outputs</Box>}
            />
          ),
        },
        {
          id: 'parameters', label: 'Parameters',
          content: (
            <Table variant="embedded" items={stack.Parameters ?? []}
              columnDefinitions={[
                { id: 'key', header: 'Key', cell: (i: any) => i.ParameterKey },
                { id: 'value', header: 'Value', cell: (i: any) => i.ParameterValue },
              ]}
              empty={<Box textAlign="center" color="text-body-secondary">No parameters</Box>}
            />
          ),
        },
        {
          id: 'resources', label: 'Resources',
          content: resLoading ? <Spinner /> : resError ? (
            <SpaceBetween size="s">
              <Box color="text-status-error">Error: {resError}</Box>
              <Button onClick={fetchResources}>Refresh Resources</Button>
            </SpaceBetween>
          ) : (
            <Table variant="embedded" items={resources}
              columnDefinitions={[
                { id: 'type', header: 'Type', cell: i => i.ResourceType },
                { id: 'logical', header: 'Logical ID', cell: i => i.LogicalResourceId },
                { id: 'physical', header: 'Physical ID', cell: i => i.PhysicalResourceId },
                { id: 'status', header: 'Status', cell: i => <StatusIndicator type={statusType(i.ResourceStatus)}>{i.ResourceStatus}</StatusIndicator> },
                { id: 'updated', header: 'Last Updated', cell: i => i.LastUpdatedTimestamp ? new Date(i.LastUpdatedTimestamp).toLocaleString() : '—' },
              ]}
              empty={<Box textAlign="center" color="text-body-secondary">No resources</Box>}
            />
          ),
        },
        {
          id: 'costs', label: 'Costs',
          content: costLoading ? <Spinner /> : costError ? (
            <SpaceBetween size="s">
              <Box color="text-status-error">{costError}</Box>
              <Button onClick={fetchCosts}>Retry</Button>
            </SpaceBetween>
          ) : (
            <SpaceBetween size="s">
              {costRows.length > 0 && <Box variant="h3">Total: ${totalCost}</Box>}
              {costRows.length > 0 && <BarChart
                series={[{
                  title: 'Cost',
                  type: 'bar',
                  data: Object.entries(costRows.reduce<Record<string, number>>((acc, r) => {
                    acc[r.service] = (acc[r.service] ?? 0) + parseFloat(r.cost);
                    return acc;
                  }, {})).map(([service, cost]) => ({ x: service, y: cost })),
                }]}
                xTitle="Service"
                yTitle="Cost ($)"
                hideFilter
                ariaLabel="Cost by Service"
              />}
              <Table variant="embedded" items={costRows}
                columnDefinitions={[
                  { id: 'service', header: 'Service', cell: i => i.service },
                  { id: 'cost', header: 'Cost ($)', cell: i => i.cost },
                  { id: 'period', header: 'Period', cell: i => i.period },
                ]}
                empty={<Box textAlign="center" color="text-body-secondary">No cost data</Box>}
              />
            </SpaceBetween>
          ),
        },
        {
          id: 'inventory', label: 'Cached Inventory',
          content: invLoading ? <Spinner /> : (
            <Table variant="embedded" items={inventoryItems}
              columnDefinitions={[
                { id: 'name', header: 'Name', cell: (i: any) => <Link to={`/inventory/${i.type}/${i.documentId}`}>{i.name}</Link> },
                { id: 'type', header: 'Type', cell: (i: any) => i.type },
                { id: 'status', header: 'Status', cell: (i: any) => i.status },
              ]}
              empty={<Box textAlign="center" color="text-body-secondary">No cached inventory items</Box>}
            />
          ),
        },
      ]} />
    </SpaceBetween>
  );
}
